import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminData } from '../../admin/AdminDataContext';
import { LayerAccessEditor, type AccessRow } from '../../components/admin/LayerAccessEditor';
import { DataTypeSelect } from '../../components/shared/DataTypeSelect';
import { api, ApiError } from '../../lib/api';
import { basemapsApi } from '../../lib/basemapsApi';
import { sanitizeCode, slugify } from '../../lib/codeUtils';
import { dictionariesApi, type DictionaryDto } from '../../lib/dictionariesApi';
import {
  layersApi,
  type DbTableInfo,
  type FieldDataType,
  type ImportAnalyzeResult,
  type LayerGeometryType,
  type LayerPermissionEffect,
  type LayerPermissionKind,
  type OgcLayerInfo,
  type OgcServiceType,
} from '../../lib/layersApi';
import { decodeWorkspaceId } from '../../lib/tokens';
import type { Group, RolesOverviewMember } from '../../lib/types';
import { isWorkspaceAdminGroup, isWorkspaceAdminMember } from '../../lib/workspaceAdmin';

type SourceKind = 'file' | 'ogc' | 'postgis' | 'rest' | 'empty' | 'copy' | 'raster' | 'ai';

type FileStep = 'file-upload' | 'file-structure' | 'file-semantics' | 'file-style' | 'file-access' | 'file-publish';
type EmptyStep = 'empty-structure' | 'empty-fields' | 'empty-style' | 'empty-access' | 'empty-publish';
type WizardStep =
  | 'source'
  | 'ogc-connection'
  | 'ogc-wms-publish'
  | 'ogc-wmts-publish'
  | 'db-connection'
  | 'raster-connection'
  | 'raster-publish'
  | 'arcgis-connection'
  | FileStep
  | EmptyStep;

const dbTableKey = (t: DbTableInfo) => `${t.schema_name}.${t.table_name}.${t.geometry_column}`;

const ogcServiceOptions: { value: OgcServiceType; label: string; hint: string }[] = [
  { value: 'wfs', label: 'WFS', hint: 'Векторные объекты — импортируются как обычный слой' },
  { value: 'wms', label: 'WMS', hint: 'Растровая карта — публикуется как подложка поверх карты' },
  { value: 'ogc_api', label: 'OGC API Features', hint: 'Векторные объекты (JSON) — импортируются как обычный слой' },
  { value: 'wmts', label: 'WMTS', hint: 'Только просмотр списка слоёв — публикация появится позже' },
];

const geometryTypeOptions: { value: LayerGeometryType; label: string }[] = [
  { value: 'point', label: 'Точка' },
  { value: 'multipoint', label: 'Мультиточка' },
  { value: 'line', label: 'Линия' },
  { value: 'multiline', label: 'Мультилиния' },
  { value: 'polygon', label: 'Полигон' },
  { value: 'multipolygon', label: 'Мультиполигон' },
];

type FieldOverrideState = {
  code: string;
  name: string;
  data_type: FieldDataType;
  dictionary_id?: string | null;
  include: boolean;
  searchable: boolean;
  filterable: boolean;
  visible_in_table: boolean;
  visible_in_popup: boolean;
  required: boolean;
  /** Empty-layer field builder only: true once the user has manually edited the
   * code, so further edits to the name stop overwriting it. */
  codeTouched?: boolean;
};

const stepLabels = ['Источник', 'Подключение', 'Структура', 'Семантика', 'Стиль', 'Доступ', 'Публикация'];

const stepIndex: Record<WizardStep, number> = {
  source: 0,
  'ogc-connection': 1,
  'ogc-wms-publish': 6,
  'ogc-wmts-publish': 6,
  'db-connection': 1,
  'raster-connection': 1,
  'raster-publish': 6,
  'arcgis-connection': 1,
  'file-upload': 1,
  'file-structure': 2,
  'file-semantics': 3,
  'file-style': 4,
  'file-access': 5,
  'file-publish': 6,
  'empty-structure': 2,
  'empty-fields': 3,
  'empty-style': 4,
  'empty-access': 5,
  'empty-publish': 6,
};

const stepTitles: Record<WizardStep, string> = {
  source: 'Добавление слоя',
  'ogc-connection': 'Подключение OGC-сервиса',
  'ogc-wms-publish': 'Публикация WMS-слоя',
  'ogc-wmts-publish': 'Публикация WMTS-подложки',
  'db-connection': 'Подключение к PostgreSQL / PostGIS',
  'raster-connection': 'Растровый источник',
  'raster-publish': 'Публикация растрового слоя',
  'arcgis-connection': 'Подключение ArcGIS REST',
  'file-upload': 'Загрузка файла',
  'file-structure': 'Структура слоя',
  'file-semantics': 'Семантика полей',
  'file-style': 'Стиль слоя',
  'file-access': 'Доступ к слою',
  'file-publish': 'Публикация',
  'empty-structure': 'Структура слоя',
  'empty-fields': 'Поля слоя',
  'empty-style': 'Стиль слоя',
  'empty-access': 'Доступ к слою',
  'empty-publish': 'Публикация',
};

const colorPalette = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#06b6d4', '#475569', '#db2777'];

const sourceCards: {
  kind: SourceKind;
  icon: string;
  iconClass: string;
  title: string;
  description: string;
  tag: string;
  tagClass: string;
  available: boolean;
}[] = [
  {
    kind: 'file',
    icon: '⇧',
    iconClass: 'bg-action-primary',
    title: 'Файл',
    description: 'Shapefile (ZIP), GeoJSON',
    tag: 'Вектор',
    tagClass: 'text-action-primary',
    available: true,
  },
  {
    kind: 'ogc',
    icon: '◎',
    iconClass: 'bg-status-info',
    title: 'OGC-сервисы',
    description: 'WMS, WFS, WMTS, OGC API Features',
    tag: 'Внешний источник',
    tagClass: 'text-status-info',
    available: true,
  },
  {
    kind: 'postgis',
    icon: 'DB',
    iconClass: 'bg-status-success',
    title: 'PostgreSQL / PostGIS',
    description: 'Таблицы и представления с геометрией',
    tag: 'Прямое подключение',
    tagClass: 'text-status-success',
    available: true,
  },
  {
    kind: 'rest',
    icon: 'API',
    iconClass: 'bg-ai-accent',
    title: 'REST / ArcGIS',
    description: 'ArcGIS MapServer / FeatureServer',
    tag: 'API-интеграция',
    tagClass: 'text-ai-accent',
    available: true,
  },
  {
    kind: 'empty',
    icon: '+',
    iconClass: 'bg-bg-surface-secondary text-text-primary',
    title: 'Пустой слой',
    description: 'Создать структуру и геометрию вручную',
    tag: 'Внутренний слой',
    tagClass: 'text-text-secondary',
    available: true,
  },
  {
    kind: 'copy',
    icon: '⧉',
    iconClass: 'bg-bg-surface-secondary text-text-primary',
    title: 'Копия слоя',
    description: 'Клонировать структуру, стиль или данные',
    tag: 'Внутренний слой',
    tagClass: 'text-text-secondary',
    available: false,
  },
  {
    kind: 'raster',
    icon: '▧',
    iconClass: 'bg-status-warning',
    title: 'Растр и тайлы',
    description: 'XYZ, TMS, MBTiles — по URL-шаблону тайлов',
    tag: 'Слой · покрытие',
    tagClass: 'text-status-warning',
    available: true,
  },
  {
    kind: 'ai',
    icon: '✦',
    iconClass: 'bg-ai-accent',
    title: 'Результаты ИИ',
    description: 'Детекции, сегментация, зоны риска',
    tag: 'AI-слой',
    tagClass: 'text-ai-accent',
    available: false,
  },
];

export function AddLayerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/app/data';
  const returnLabel = returnTo === '/app' ? '← Карта' : '← Данные и слои';
  const { mapSettings, layerGroups } = useAdminData();

  const [dictionaries, setDictionaries] = useState<DictionaryDto[]>([]);
  useEffect(() => {
    dictionariesApi.listDictionaries().then(setDictionaries).catch(() => {});
  }, []);

  // Права доступа настраиваются на шаге "Доступ" до того, как слой вообще
  // существует — накапливаются здесь как черновик и применяются одним циклом
  // POST /layers/{id}/permissions сразу после успешного создания слоя.
  const [accessRows, setAccessRows] = useState<AccessRow[]>([]);
  const [accessMembers, setAccessMembers] = useState<RolesOverviewMember[]>([]);
  const [accessGroups, setAccessGroups] = useState<Group[]>([]);
  useEffect(() => {
    const workspaceId = decodeWorkspaceId();
    api
      .getRolesOverview()
      .then((overview) => {
        setAccessMembers(overview.members.filter((m) => !isWorkspaceAdminMember(m, workspaceId)));
        setAccessGroups(overview.groups.filter((g) => !isWorkspaceAdminGroup(g, workspaceId)));
      })
      .catch(() => {});
  }, []);

  const handleAddAccessRow = (
    principalType: 'user' | 'group',
    principalId: string,
    principalName: string,
    permission: LayerPermissionKind,
    effect: LayerPermissionEffect,
  ) => {
    setAccessRows((prev) => [
      ...prev,
      { id: `draft_${Date.now()}_${Math.round(Math.random() * 1000)}`, principal_type: principalType, principal_id: principalId, principal_name: principalName, permission, effect },
    ]);
  };

  const handleRemoveAccessRow = (rowId: string) => setAccessRows((prev) => prev.filter((r) => r.id !== rowId));

  /** Best-effort — the layer itself already got created successfully by the time
   * this runs, so a failed permission grant shouldn't block navigating away. */
  const flushAccessRows = async (layerId: string) => {
    for (const row of accessRows) {
      try {
        await layersApi.grantLayerPermission(layerId, row.principal_type, row.principal_id, row.permission, row.effect);
      } catch {
        // сам слой уже создан — молча пропускаем, права можно донастроить позже через «Права доступа»
      }
    }
  };

  const [step, setStep] = useState<WizardStep>('source');
  const [selectedKind, setSelectedKind] = useState<SourceKind>('file');
  const selectedCard = sourceCards.find((c) => c.kind === selectedKind)!;

  const [ogcServiceType, setOgcServiceType] = useState<OgcServiceType>('wfs');
  const [ogcUrl, setOgcUrl] = useState('');
  const [ogcUsername, setOgcUsername] = useState('');
  const [ogcPassword, setOgcPassword] = useState('');
  const [ogcTestStatus, setOgcTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [ogcTestError, setOgcTestError] = useState<string | null>(null);
  const [ogcLayers, setOgcLayers] = useState<OgcLayerInfo[]>([]);
  const [ogcTotal, setOgcTotal] = useState(0);
  const [ogcVersion, setOgcVersion] = useState<string | null>(null);
  const [ogcLayerSearch, setOgcLayerSearch] = useState('');
  const [selectedOgcLayerName, setSelectedOgcLayerName] = useState<string | null>(null);
  const [ogcAnalyzing, setOgcAnalyzing] = useState(false);
  const [ogcAnalyzeError, setOgcAnalyzeError] = useState<string | null>(null);
  const [wmsOpacity, setWmsOpacity] = useState(1);
  const [wmsCommitting, setWmsCommitting] = useState(false);
  const [wmsCommitError, setWmsCommitError] = useState<string | null>(null);
  const [wmtsPublishMode, setWmtsPublishMode] = useState<'layer' | 'basemap'>('layer');
  const [wmtsBasemapName, setWmtsBasemapName] = useState('');
  const [wmtsIsDefault, setWmtsIsDefault] = useState(false);
  const [wmtsOpacity, setWmtsOpacity] = useState(1);
  const [wmtsCommitting, setWmtsCommitting] = useState(false);
  const [wmtsCommitError, setWmtsCommitError] = useState<string | null>(null);

  const [rasterUrlTemplate, setRasterUrlTemplate] = useState('');
  const [rasterTileSize, setRasterTileSize] = useState<256 | 512>(256);
  const [rasterMinZoom, setRasterMinZoom] = useState(0);
  const [rasterMaxZoom, setRasterMaxZoom] = useState(19);
  const [rasterTemplateError, setRasterTemplateError] = useState<string | null>(null);
  const [rasterOpacity, setRasterOpacity] = useState(1);
  const [rasterCommitting, setRasterCommitting] = useState(false);
  const [rasterCommitError, setRasterCommitError] = useState<string | null>(null);

  const [arcgisUrl, setArcgisUrl] = useState('');
  const [arcgisTestStatus, setArcgisTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [arcgisTestError, setArcgisTestError] = useState<string | null>(null);
  const [arcgisLayers, setArcgisLayers] = useState<OgcLayerInfo[]>([]);
  const [arcgisTileUrlTemplate, setArcgisTileUrlTemplate] = useState<string | null>(null);
  const [arcgisLayerSearch, setArcgisLayerSearch] = useState('');
  const [selectedArcgisLayerName, setSelectedArcgisLayerName] = useState<string | null>(null);
  const [arcgisAnalyzing, setArcgisAnalyzing] = useState(false);
  const [arcgisAnalyzeError, setArcgisAnalyzeError] = useState<string | null>(null);
  const selectedArcgisLayer = arcgisLayers.find((l) => l.name === selectedArcgisLayerName) ?? null;
  const filteredArcgisLayers = useMemo(() => {
    const q = arcgisLayerSearch.trim().toLowerCase();
    if (!q) return arcgisLayers;
    return arcgisLayers.filter((l) => l.name.toLowerCase().includes(q) || l.title.toLowerCase().includes(q));
  }, [arcgisLayers, arcgisLayerSearch]);
  const selectedOgcLayer = ogcLayers.find((l) => l.name === selectedOgcLayerName) ?? null;
  const filteredOgcLayers = useMemo(() => {
    const q = ogcLayerSearch.trim().toLowerCase();
    if (!q) return ogcLayers;
    return ogcLayers.filter((l) => l.name.toLowerCase().includes(q) || l.title.toLowerCase().includes(q));
  }, [ogcLayers, ogcLayerSearch]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [analyzeResult, setAnalyzeResult] = useState<ImportAnalyzeResult | null>(null);
  const [fieldOverrides, setFieldOverrides] = useState<FieldOverrideState[]>([]);
  const [layerName, setLayerName] = useState('');
  const [layerCode, setLayerCode] = useState('');
  const [layerGroup, setLayerGroup] = useState('');
  const [styleColor, setStyleColor] = useState(colorPalette[0]);
  const [styleOpacity, setStyleOpacity] = useState(0.6);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  const [emptyGeometryType, setEmptyGeometryType] = useState<LayerGeometryType>('point');
  const [emptyFields, setEmptyFields] = useState<FieldOverrideState[]>([]);
  const [creatingLayer, setCreatingLayer] = useState(false);
  const [createLayerError, setCreateLayerError] = useState<string | null>(null);

  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [exitPrompt, setExitPrompt] = useState(false);

  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState(5432);
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbSslMode, setDbSslMode] = useState<'disable' | 'prefer' | 'require'>('prefer');
  const [dbTestStatus, setDbTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [dbTestError, setDbTestError] = useState<string | null>(null);
  const [dbTables, setDbTables] = useState<DbTableInfo[]>([]);
  const [selectedDbTableKey, setSelectedDbTableKey] = useState<string | null>(null);
  const [dbAnalyzing, setDbAnalyzing] = useState(false);
  const [dbAnalyzeError, setDbAnalyzeError] = useState<string | null>(null);
  const selectedDbTable = dbTables.find((t) => dbTableKey(t) === selectedDbTableKey) ?? null;

  const handleTestOgcConnection = async () => {
    if (!ogcUrl.trim()) return;
    setOgcTestStatus('testing');
    setOgcTestError(null);
    setOgcLayers([]);
    setOgcTotal(0);
    setSelectedOgcLayerName(null);
    try {
      const result = await layersApi.testOgcConnection({
        url: ogcUrl.trim(),
        service: ogcServiceType,
        username: ogcUsername.trim() || undefined,
        password: ogcUsername.trim() ? ogcPassword : undefined,
      });
      if (result.ok) {
        setOgcTestStatus('success');
        setOgcLayers(result.layers);
        setOgcTotal(result.total);
        setOgcVersion(result.version);
      } else {
        setOgcTestStatus('error');
        setOgcTestError(result.error ?? 'Не удалось подключиться.');
      }
    } catch (err) {
      setOgcTestStatus('error');
      setOgcTestError(err instanceof ApiError ? err.message : 'Не удалось подключиться.');
    }
  };

  const handleAnalyzeOgcLayer = async () => {
    if (!selectedOgcLayer) return;
    setOgcAnalyzing(true);
    setOgcAnalyzeError(null);
    try {
      const result = await layersApi.analyzeOgcLayer({
        connection: {
          url: ogcUrl.trim(),
          service: ogcServiceType,
          username: ogcUsername.trim() || undefined,
          password: ogcUsername.trim() ? ogcPassword : undefined,
        },
        layer_name: selectedOgcLayer.name,
      });
      setAnalyzeResult(result);
      setFieldOverrides(
        result.fields.map((f) => ({
          ...f,
          include: true,
          searchable: true,
          filterable: false,
          visible_in_table: true,
          visible_in_popup: true,
          required: false,
        })),
      );
      setLayerName((prev) => prev || selectedOgcLayer.title);
      setLayerCode((prev) => prev || slugify(selectedOgcLayer.name, 'layer'));
      setStep('file-structure');
    } catch (err) {
      setOgcAnalyzeError(err instanceof ApiError ? err.message : 'Не удалось получить объекты слоя.');
    } finally {
      setOgcAnalyzing(false);
    }
  };

  const handleOgcNext = () => {
    if (!selectedOgcLayer) return;
    if (ogcServiceType === 'wms') {
      setLayerName((prev) => prev || selectedOgcLayer.title);
      setLayerCode((prev) => prev || slugify(selectedOgcLayer.name, 'layer'));
      setStep('ogc-wms-publish');
    } else if (ogcServiceType === 'wmts') {
      setWmtsBasemapName((prev) => prev || selectedOgcLayer.title);
      setLayerName((prev) => prev || selectedOgcLayer.title);
      setLayerCode((prev) => prev || slugify(selectedOgcLayer.name, 'layer'));
      setWmtsCommitError(null);
      setStep('ogc-wmts-publish');
    } else {
      handleAnalyzeOgcLayer();
    }
  };

  const handleCommitWmsLayer = async () => {
    if (!selectedOgcLayer || !mapSettings?.id) return;
    setWmsCommitError(null);
    setWmsCommitting(true);
    try {
      await layersApi.commitWmsLayer({
        connection: {
          url: ogcUrl.trim(),
          service: 'wms',
          username: ogcUsername.trim() || undefined,
          password: ogcUsername.trim() ? ogcPassword : undefined,
        },
        layer_name: selectedOgcLayer.name,
        layer_title: selectedOgcLayer.title,
        map_id: mapSettings.id,
        name: layerName.trim(),
        code: layerCode.trim(),
        group: layerGroup.trim() || undefined,
        opacity: wmsOpacity,
      });
      navigate(returnTo);
    } catch (err) {
      setWmsCommitError(err instanceof ApiError ? err.message : 'Не удалось опубликовать слой.');
    } finally {
      setWmsCommitting(false);
    }
  };

  const handleCommitWmts = async () => {
    if (!selectedOgcLayer) return;
    const connection = {
      url: ogcUrl.trim(),
      service: 'wmts' as const,
      username: ogcUsername.trim() || undefined,
      password: ogcUsername.trim() ? ogcPassword : undefined,
    };
    setWmtsCommitError(null);
    setWmtsCommitting(true);
    try {
      if (wmtsPublishMode === 'basemap') {
        if (!wmtsBasemapName.trim()) return;
        await basemapsApi.commitWmtsBasemap({
          connection,
          layer_name: selectedOgcLayer.name,
          name: wmtsBasemapName.trim(),
          is_default: wmtsIsDefault,
        });
      } else {
        if (!mapSettings?.id || !layerName.trim() || !layerCode.trim()) return;
        await layersApi.commitWmtsLayer({
          connection,
          layer_name: selectedOgcLayer.name,
          layer_title: selectedOgcLayer.title,
          map_id: mapSettings.id,
          name: layerName.trim(),
          code: layerCode.trim(),
          group: layerGroup.trim() || undefined,
          opacity: wmtsOpacity,
        });
      }
      navigate(returnTo);
    } catch (err) {
      setWmtsCommitError(err instanceof ApiError ? err.message : 'Не удалось опубликовать WMTS.');
    } finally {
      setWmtsCommitting(false);
    }
  };

  const handleRasterNext = () => {
    const t = rasterUrlTemplate.trim();
    if (!t.includes('{z}') || !t.includes('{x}') || (!t.includes('{y}') && !t.includes('{-y}'))) {
      setRasterTemplateError('Шаблон URL должен содержать {z}, {x} и {y} (или {-y} для TMS)');
      return;
    }
    setRasterTemplateError(null);
    setStep('raster-publish');
  };

  const handleCommitRaster = async () => {
    if (!mapSettings?.id || !layerName.trim() || !layerCode.trim()) return;
    setRasterCommitError(null);
    setRasterCommitting(true);
    try {
      await layersApi.commitRasterLayer({
        map_id: mapSettings.id,
        name: layerName.trim(),
        code: layerCode.trim(),
        group: layerGroup.trim() || undefined,
        opacity: rasterOpacity,
        url_template: rasterUrlTemplate.trim(),
        tile_size: rasterTileSize,
        min_zoom: rasterMinZoom,
        max_zoom: rasterMaxZoom,
        tms: rasterUrlTemplate.includes('{-y}'),
      });
      navigate(returnTo);
    } catch (err) {
      setRasterCommitError(err instanceof ApiError ? err.message : 'Не удалось опубликовать слой.');
    } finally {
      setRasterCommitting(false);
    }
  };

  const handleTestArcgisConnection = async () => {
    if (!arcgisUrl.trim()) return;
    setArcgisTestStatus('testing');
    setArcgisTestError(null);
    setArcgisLayers([]);
    setArcgisTileUrlTemplate(null);
    setSelectedArcgisLayerName(null);
    try {
      const result = await layersApi.testArcgisConnection(arcgisUrl.trim());
      if (result.ok) {
        setArcgisTestStatus('success');
        setArcgisLayers(result.layers);
        setArcgisTileUrlTemplate(result.tile_url_template);
      } else {
        setArcgisTestStatus('error');
        setArcgisTestError(result.error ?? 'Не удалось подключиться.');
      }
    } catch (err) {
      setArcgisTestStatus('error');
      setArcgisTestError(err instanceof ApiError ? err.message : 'Не удалось подключиться.');
    }
  };

  const handleAnalyzeArcgisLayer = async () => {
    if (!selectedArcgisLayer) return;
    setArcgisAnalyzing(true);
    setArcgisAnalyzeError(null);
    try {
      const result = await layersApi.analyzeArcgisLayer(arcgisUrl.trim(), selectedArcgisLayer.name);
      setAnalyzeResult(result);
      setFieldOverrides(
        result.fields.map((f) => ({
          ...f,
          include: true,
          searchable: true,
          filterable: false,
          visible_in_table: true,
          visible_in_popup: true,
          required: false,
        })),
      );
      setLayerName((prev) => prev || selectedArcgisLayer.title);
      setLayerCode((prev) => prev || slugify(selectedArcgisLayer.title, 'layer'));
      setStep('file-structure');
    } catch (err) {
      setArcgisAnalyzeError(err instanceof ApiError ? err.message : 'Не удалось получить объекты слоя.');
    } finally {
      setArcgisAnalyzing(false);
    }
  };

  const handlePublishArcgisTiles = () => {
    if (!arcgisTileUrlTemplate) return;
    setRasterUrlTemplate(arcgisTileUrlTemplate);
    setRasterTileSize(256);
    setRasterMinZoom(0);
    setRasterMaxZoom(19);
    setLayerName((prev) => prev || 'ArcGIS тайлы');
    setLayerCode((prev) => prev || slugify('arcgis-tiles', 'layer'));
    setRasterTemplateError(null);
    setStep('raster-publish');
  };

  const handleFileSelected = async (file: File) => {
    setFileName(file.name);
    setAnalyzeError(null);
    setAnalyzeResult(null);
    setAnalyzing(true);
    try {
      const result = await layersApi.analyzeImport(file);
      setAnalyzeResult(result);
      setFieldOverrides(
        result.fields.map((f) => ({
          ...f,
          include: true,
          searchable: true,
          filterable: false,
          visible_in_table: true,
          visible_in_popup: true,
          required: false,
        })),
      );
      const baseName = file.name.replace(/\.(zip|geojson|json)$/i, '');
      setLayerName((prev) => prev || baseName);
      setLayerCode((prev) => prev || slugify(baseName, 'layer'));
      setStep('file-structure');
    } catch (err) {
      setAnalyzeError(err instanceof ApiError ? err.message : 'Не удалось разобрать файл.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleTestDbConnection = async () => {
    setDbTestStatus('testing');
    setDbTestError(null);
    setDbTables([]);
    setSelectedDbTableKey(null);
    try {
      const result = await layersApi.testDbConnection({
        host: dbHost.trim(),
        port: dbPort,
        database: dbName.trim(),
        username: dbUser.trim(),
        password: dbPassword,
        ssl_mode: dbSslMode,
      });
      if (result.ok) {
        setDbTestStatus('success');
        setDbTables(result.tables);
      } else {
        setDbTestStatus('error');
        setDbTestError(result.error ?? 'Не удалось подключиться.');
      }
    } catch (err) {
      setDbTestStatus('error');
      setDbTestError(err instanceof ApiError ? err.message : 'Не удалось подключиться.');
    }
  };

  const handleAnalyzeDbTable = async () => {
    if (!selectedDbTable) return;
    setDbAnalyzing(true);
    setDbAnalyzeError(null);
    try {
      const result = await layersApi.analyzeDbTable({
        connection: {
          host: dbHost.trim(),
          port: dbPort,
          database: dbName.trim(),
          username: dbUser.trim(),
          password: dbPassword,
          ssl_mode: dbSslMode,
        },
        schema_name: selectedDbTable.schema_name,
        table_name: selectedDbTable.table_name,
        geometry_column: selectedDbTable.geometry_column,
        srid: selectedDbTable.srid,
      });
      setAnalyzeResult(result);
      setFieldOverrides(
        result.fields.map((f) => ({
          ...f,
          include: true,
          searchable: true,
          filterable: false,
          visible_in_table: true,
          visible_in_popup: true,
          required: false,
        })),
      );
      setLayerName((prev) => prev || selectedDbTable.table_name);
      setLayerCode((prev) => prev || slugify(selectedDbTable.table_name, 'layer'));
      setStep('file-structure');
    } catch (err) {
      setDbAnalyzeError(err instanceof ApiError ? err.message : 'Не удалось прочитать таблицу.');
    } finally {
      setDbAnalyzing(false);
    }
  };

  const handleCommitFileImport = async () => {
    if (!analyzeResult || !mapSettings?.id) return;
    setCommitError(null);
    setCommitting(true);
    try {
      // Не кладём в AdminDataContext.layers (localStorage/OGC-реестр) — этот слой уже
      // придёт на карту через реальный бэкенд (initialRealLayers), а положить его ещё
      // и сюда означало бы отрисовать его дважды (настоящая геометрия + синтетическая
      // заглушка поверх неё с тем же id). DataLayersPage сам подгружает реальные слои.
      const created = await layersApi.commitImport({
        token: analyzeResult.token,
        map_id: mapSettings.id,
        name: layerName.trim(),
        code: layerCode.trim(),
        group: layerGroup.trim() || undefined,
        fields: fieldOverrides,
        style: { color: styleColor, opacity: styleOpacity, stroke_width: strokeWidth },
      });
      await flushAccessRows(created.id);
      navigate(returnTo);
    } catch (err) {
      setCommitError(err instanceof ApiError ? err.message : 'Не удалось сохранить слой.');
    } finally {
      setCommitting(false);
    }
  };

  const addEmptyField = () => {
    setEmptyFields((prev) => [
      ...prev,
      {
        code: `field_${prev.length + 1}`,
        name: '',
        data_type: 'string',
        include: true,
        searchable: true,
        filterable: false,
        visible_in_table: true,
        visible_in_popup: true,
        required: false,
        codeTouched: false,
      },
    ]);
  };

  const removeEmptyField = (index: number) => setEmptyFields((prev) => prev.filter((_, i) => i !== index));

  const handleCreateEmptyLayer = async () => {
    if (!mapSettings?.id) return;
    setCreateLayerError(null);
    setCreatingLayer(true);
    try {
      // Те же соображения, что и в handleCommitFileImport — слой уже придёт на карту
      // через реальный бэкенд, дублировать его в AdminDataContext.layers не нужно.
      const created = await layersApi.createLayer({
        map_id: mapSettings.id,
        name: layerName.trim(),
        code: layerCode.trim(),
        group: layerGroup.trim() || undefined,
        geometry_type: emptyGeometryType,
        fields: emptyFields.map(({ code, name, data_type, dictionary_id, searchable, filterable, visible_in_table, visible_in_popup, required }) => ({
          code,
          name,
          data_type,
          dictionary_id,
          searchable,
          filterable,
          visible_in_table,
          visible_in_popup,
          required,
        })),
        style: { color: styleColor, opacity: styleOpacity, stroke_width: strokeWidth },
      });
      await flushAccessRows(created.id);
      navigate(returnTo);
    } catch (err) {
      setCreateLayerError(err instanceof ApiError ? err.message : 'Не удалось создать слой.');
    } finally {
      setCreatingLayer(false);
    }
  };

  /** Only the "empty layer" path — its steps after "structure" (fields/style/access)
   * are all things EditLayerPage's tabs already cover, so a saved draft can be
   * finished later from "Данные и слои" without any special resume flow. */
  const isEmptyDraftStep = selectedKind === 'empty' && step !== 'source';
  const hasEmptyDraftProgress = isEmptyDraftStep && layerName.trim() !== '';

  const handleSaveDraft = async () => {
    if (!mapSettings?.id || !layerName.trim() || !layerCode.trim()) return;
    setDraftError(null);
    setSavingDraft(true);
    try {
      const created = await layersApi.createLayer({
        map_id: mapSettings.id,
        name: layerName.trim(),
        code: layerCode.trim(),
        group: layerGroup.trim() || undefined,
        geometry_type: emptyGeometryType,
        fields: emptyFields.map(({ code, name, data_type, dictionary_id, searchable, filterable, visible_in_table, visible_in_popup, required }) => ({
          code,
          name,
          data_type,
          dictionary_id,
          searchable,
          filterable,
          visible_in_table,
          visible_in_popup,
          required,
        })),
        style: { color: styleColor, opacity: styleOpacity, stroke_width: strokeWidth },
        status: 'draft',
      });
      await flushAccessRows(created.id);
      navigate(returnTo);
    } catch (err) {
      setDraftError(err instanceof ApiError ? err.message : 'Не удалось сохранить черновик.');
      setExitPrompt(false);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCloseClick = (e: MouseEvent) => {
    if (hasEmptyDraftProgress) {
      e.preventDefault();
      setExitPrompt(true);
    }
  };

  const includedFieldCount = fieldOverrides.filter((f) => f.include).length;

  return (
    <div className="flex h-screen w-full flex-col overflow-y-auto bg-bg-canvas">
      <header className="flex h-[72px] shrink-0 items-center gap-4 border-b border-border-default bg-bg-surface px-8">
        {step === 'source' ? (
          <Link to={returnTo} className="text-sm font-medium text-text-secondary hover:text-text-primary">
            {returnLabel}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setStep('source')}
            className="text-sm font-medium text-text-secondary hover:text-text-primary"
          >
            ← Добавление слоя
          </button>
        )}
        <h1 className="text-[17px] font-semibold text-text-primary">{stepTitles[step]}</h1>
        <div className="ml-auto flex items-center gap-3">
          {draftError && <p className="max-w-[280px] text-xs font-medium text-status-danger">{draftError}</p>}
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!isEmptyDraftStep || !layerName.trim() || !layerCode.trim() || savingDraft}
            title={isEmptyDraftStep ? 'Сохранить и продолжить позже' : 'Черновики доступны только для пустого слоя'}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {savingDraft ? 'Сохраняем…' : 'Сохранить черновик'}
          </button>
          <Link
            to={returnTo}
            onClick={handleCloseClick}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Закрыть
          </Link>
        </div>
      </header>

      {exitPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[360px] rounded-2xl bg-bg-surface p-6 shadow-xl">
            <h2 className="text-base font-semibold text-text-primary">Сохранить как черновик?</h2>
            <p className="mt-2 text-sm text-text-secondary">
              Слой «{layerName}» ещё не создан. Можно сохранить введённые данные как черновик и вернуться к нему позже.
            </p>
            {draftError && <p className="mt-2 text-xs font-medium text-status-danger">{draftError}</p>}
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="flex h-10 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingDraft ? 'Сохраняем…' : 'Сохранить черновик'}
              </button>
              <button
                type="button"
                onClick={() => navigate(returnTo)}
                disabled={savingDraft}
                className="flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Не сохранять
              </button>
              <button
                type="button"
                onClick={() => setExitPrompt(false)}
                disabled={savingDraft}
                className="flex h-10 items-center justify-center rounded-[10px] text-[13px] font-medium text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-[66px] shrink-0 items-center gap-8 border-b border-border-default bg-bg-surface px-8">
        {stepLabels.map((label, i) => {
          const current = stepIndex[step];
          const isDone = i < current;
          const isCurrent = i === current;
          return (
            <div key={label} className="flex items-center gap-2">
              <span
                className={`flex size-[30px] items-center justify-center rounded-full text-xs font-semibold ${
                  isDone
                    ? 'bg-status-success text-text-inverse'
                    : isCurrent
                      ? 'bg-action-primary text-text-inverse'
                      : 'bg-bg-surface-secondary text-text-secondary'
                }`}
              >
                {isDone ? '✓' : i + 1}
              </span>
              <span className={`text-xs font-medium ${isCurrent ? 'text-action-primary font-semibold' : 'text-text-secondary'}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {step === 'source' && (
        <div className="flex-1 px-8 py-8">
          <h2 className="text-[26px] font-semibold text-text-primary">Выберите источник данных</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Источник определяет способ хранения, обновления и доступные возможности слоя
          </p>

          <div className="mt-6 grid grid-cols-[1fr_412px] gap-6">
            <div className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <p className="text-[15px] font-semibold text-text-primary">Доступные источники</p>
              <div className="mt-4 grid grid-cols-2 gap-4">
                {sourceCards.map((card) => (
                  <button
                    key={card.kind}
                    type="button"
                    onClick={() => setSelectedKind(card.kind)}
                    title={card.available ? undefined : `${card.title} — появится в следующих версиях`}
                    className={`relative rounded-[14px] border p-5 text-left transition-colors ${
                      selectedKind === card.kind
                        ? 'border-action-primary bg-bg-surface-secondary'
                        : 'border-border-default bg-bg-surface hover:bg-bg-surface-secondary'
                    } ${card.available ? '' : 'opacity-70'}`}
                  >
                    {selectedKind === card.kind && (
                      <span className="absolute right-4 top-4 flex size-6 items-center justify-center rounded-xl bg-action-primary text-xs text-text-inverse">
                        ✓
                      </span>
                    )}
                    <span className={`flex size-[46px] items-center justify-center rounded-xl text-[13px] font-semibold text-text-inverse ${card.iconClass}`}>
                      {card.icon}
                    </span>
                    <p className="mt-3 text-[15px] font-semibold text-text-primary">{card.title}</p>
                    <p className="mt-1 text-xs text-text-secondary">{card.description}</p>
                    <p className={`mt-3 text-[11px] font-medium ${card.tagClass}`}>
                      {card.available ? card.tag : 'Скоро'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="h-fit rounded-2xl border border-border-default bg-bg-surface p-6">
              <p className="text-xs font-medium text-text-secondary">Выбранный источник</p>
              <h3 className="mt-2 text-xl font-semibold text-text-primary">{selectedCard.title}</h3>

              <div className="mt-4 rounded-xl bg-bg-surface-secondary p-4">
                <p className="text-xs font-semibold text-text-primary">Поддерживаемые форматы</p>
                <p className="mt-2 text-xs text-text-secondary">{selectedCard.description}</p>
              </div>

              {(selectedKind === 'ogc' || selectedKind === 'file' || selectedKind === 'empty' || selectedKind === 'postgis' || selectedKind === 'raster' || selectedKind === 'rest') && (
                <>
                  <p className="mt-5 text-[13px] font-semibold text-text-primary">Что произойдёт дальше</p>
                  <div className="mt-3 flex flex-col gap-3">
                    {(selectedKind === 'ogc'
                      ? ['Подключение к сервису', 'Получение списка слоёв (GetCapabilities)', 'Выбор слоя', 'Публикация как слой или подложка']
                      : selectedKind === 'file'
                        ? ['Загрузка и разбор файла', 'Структура, семантика полей и стиль', 'Доступ', 'Публикация — слой появится на карте']
                        : selectedKind === 'postgis'
                          ? ['Подключение к серверу PostgreSQL/PostGIS', 'Выбор таблицы с геометрией', 'Структура, семантика полей и стиль', 'Доступ', 'Публикация — снимок данных на момент импорта']
                          : selectedKind === 'raster'
                            ? ['URL-шаблон тайлов, размер и диапазон zoom', 'Название и прозрачность', 'Публикация — слой появится на карте']
                            : selectedKind === 'rest'
                              ? ['Подключение к MapServer/FeatureServer', 'Векторные слои — импорт объектов; тайлы — публикация как растр', 'Публикация']
                              : ['Тип геометрии, название и код слоя', 'Поля (атрибуты) и их типы данных', 'Стиль', 'Доступ', 'Слой создаётся пустым — объекты рисуются на карте инструментом рисования']
                    ).map((text, i) => (
                      <div key={text} className="flex items-center gap-3">
                        <span className="flex size-7 items-center justify-center rounded-full bg-bg-surface-secondary text-[11px] font-semibold text-text-secondary">
                          {i + 1}
                        </span>
                        <span className="text-xs font-medium text-text-primary">{text}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 'ogc-connection' && (
        <div className="grid flex-1 grid-cols-[440px_1fr_360px] gap-6 px-8 py-6">
          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Параметры подключения</h2>

            <label className="mt-5 text-[11px] font-medium text-text-secondary">Тип службы</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {ogcServiceOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  title={opt.hint}
                  onClick={() => {
                    setOgcServiceType(opt.value);
                    setOgcTestStatus('idle');
                    setOgcLayers([]);
                    setSelectedOgcLayerName(null);
                  }}
                  className={`rounded-[10px] border p-2.5 text-left text-xs font-medium transition-colors ${
                    ogcServiceType === opt.value
                      ? 'border-action-primary bg-bg-surface-secondary text-text-primary'
                      : 'border-border-default bg-bg-surface text-text-secondary hover:bg-bg-surface-secondary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-text-secondary">
              {ogcServiceOptions.find((o) => o.value === ogcServiceType)?.hint}
            </p>

            <label className="mt-4 text-[11px] font-medium text-text-secondary">URL сервиса</label>
            <input
              value={ogcUrl}
              onChange={(e) => {
                setOgcUrl(e.target.value);
                setOgcTestStatus('idle');
              }}
              placeholder="https://ahocevar.com/geoserver/ows"
              className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Логин (необязательно)</label>
                <input
                  value={ogcUsername}
                  onChange={(e) => {
                    setOgcUsername(e.target.value);
                    setOgcTestStatus('idle');
                  }}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Пароль</label>
                <input
                  type="password"
                  value={ogcPassword}
                  onChange={(e) => {
                    setOgcPassword(e.target.value);
                    setOgcTestStatus('idle');
                  }}
                  disabled={!ogcUsername.trim()}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary disabled:opacity-40"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleTestOgcConnection}
              disabled={!ogcUrl.trim() || ogcTestStatus === 'testing'}
              className="mt-6 flex h-10 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {ogcTestStatus === 'testing' ? 'Проверка…' : 'Проверить подключение'}
            </button>

            {ogcTestStatus === 'success' && (
              <div className="mt-3 flex h-[26px] w-fit items-center rounded-lg bg-status-success px-2.5 text-[11px] font-medium text-text-inverse">
                Соединение установлено{ogcVersion ? ` · версия ${ogcVersion}` : ''} — слоёв:{' '}
                {ogcTotal > ogcLayers.length ? `${ogcLayers.length} из ${ogcTotal}` : ogcLayers.length}
              </div>
            )}
            {ogcTestStatus === 'success' && ogcTotal > ogcLayers.length && (
              <p className="mt-2 text-[11px] text-text-secondary">
                Сервер публикует {ogcTotal} слоёв — показаны первые {ogcLayers.length}. Найдите нужный поиском в
                списке справа.
              </p>
            )}
            {ogcTestStatus === 'error' && (
              <p className="mt-3 text-xs font-medium text-status-danger">{ogcTestError}</p>
            )}

            <p className="mt-5 text-[11px] text-text-secondary">
              Пароль нигде не сохраняется — используется только на время проверки подключения и загрузки объектов.
            </p>
          </section>

          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Доступные слои</h2>
            {ogcTestStatus !== 'success' ? (
              <p className="mt-6 text-xs text-text-secondary">
                Сначала проверьте подключение — список слоёв появится после успешного GetCapabilities.
              </p>
            ) : ogcLayers.length === 0 ? (
              <p className="mt-6 text-xs text-text-secondary">Сервер не публикует ни одного слоя.</p>
            ) : (
              <>
                <div className="mt-4 flex items-center gap-2">
                  <input
                    value={ogcLayerSearch}
                    onChange={(e) => setOgcLayerSearch(e.target.value)}
                    placeholder="Поиск по названию слоя"
                    className="h-10 flex-1 rounded-[10px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                  />
                </div>

                <div className="mt-4 flex flex-col gap-1 overflow-y-auto">
                  {filteredOgcLayers.map((l) => (
                    <label
                      key={l.name}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        type="radio"
                        name="ogc-layer"
                        checked={selectedOgcLayerName === l.name}
                        onChange={() => setSelectedOgcLayerName(l.name)}
                        className="size-4 accent-action-primary"
                      />
                      <span className="flex-1 truncate">
                        <span className="block text-xs font-medium text-text-primary">{l.title}</span>
                        <span className="block text-[10px] text-text-secondary">{l.name}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
            {ogcAnalyzeError && <p className="mt-4 text-xs font-medium text-status-danger">{ogcAnalyzeError}</p>}
          </section>

          <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Выбранный слой</h2>
            {selectedOgcLayer ? (
              <>
                <p className="mt-3 text-[13px] font-semibold text-text-primary">{selectedOgcLayer.title}</p>
                <div className="mt-4 flex flex-col gap-2.5">
                  <MetaRow label="Имя на сервере" value={selectedOgcLayer.name} />
                  <MetaRow label="CRS" value={selectedOgcLayer.crs ?? '—'} />
                  <MetaRow
                    label="Способ публикации"
                    value={ogcServiceType === 'wms' ? 'Растровая подложка' : 'Импорт объектов (снимок)'}
                  />
                </div>
                {ogcServiceType === 'wmts' && (
                  <p className="mt-5 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                    WMTS публикуется как подложка карты (доступна в переключателе подложек), а не как слой в
                    «Данные и слои» — она не содержит отдельных объектов.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-4 text-xs text-text-secondary">Выберите слой в списке слева</p>
            )}
          </section>
        </div>
      )}

      {step === 'ogc-wms-publish' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-[1fr_320px] gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Название и стиль</h2>
              <label className="mt-4 text-[11px] font-medium text-text-secondary">Название</label>
              <input
                value={layerName}
                onChange={(e) => setLayerName(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Код</label>
                  <input
                    value={layerCode}
                    onChange={(e) => setLayerCode(sanitizeCode(e.target.value))}
                    placeholder="latin_letters_and_digits"
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Группа (необязательно)</label>
                  <select
                    value={layerGroup}
                    onChange={(e) => setLayerGroup(e.target.value)}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  >
                    <option value="">Без группы</option>
                    {layerGroups.map((g) => (
                      <option key={g.id} value={g.name}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="mt-5 block text-[11px] font-medium text-text-secondary">
                Прозрачность: {Math.round(wmsOpacity * 100)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={wmsOpacity}
                onChange={(e) => setWmsOpacity(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />

              {wmsCommitError && <p className="mt-4 text-xs font-medium text-status-danger">{wmsCommitError}</p>}
            </section>

            <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Источник</h2>
              <div className="mt-3 flex flex-col gap-2.5">
                <MetaRow label="Слой на сервере" value={selectedOgcLayer?.name ?? '—'} />
                <MetaRow label="Служба" value="WMS" />
                <MetaRow label="Объекты" value="Не редактируются — растровая подложка" />
              </div>
              <p className="mt-5 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                Слой запрашивает тайлы напрямую с внешнего сервера при каждом просмотре карты — данные не
                копируются.
              </p>
            </section>
          </div>
        </div>
      )}

      {step === 'ogc-wmts-publish' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-[1fr_320px] gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Способ публикации</h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWmtsPublishMode('layer')}
                  className={`rounded-[10px] border p-3 text-left transition-colors ${
                    wmtsPublishMode === 'layer'
                      ? 'border-action-primary bg-bg-surface-secondary'
                      : 'border-border-default bg-bg-surface hover:bg-bg-surface-secondary'
                  }`}
                >
                  <p className="text-xs font-semibold text-text-primary">Слой</p>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    Независимое вкл/выкл в «Данные и слои», можно держать несколько одновременно
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setWmtsPublishMode('basemap')}
                  className={`rounded-[10px] border p-3 text-left transition-colors ${
                    wmtsPublishMode === 'basemap'
                      ? 'border-action-primary bg-bg-surface-secondary'
                      : 'border-border-default bg-bg-surface hover:bg-bg-surface-secondary'
                  }`}
                >
                  <p className="text-xs font-semibold text-text-primary">Подложка</p>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    В переключателе подложек, только одна активна одновременно
                  </p>
                </button>
              </div>

              <div className="my-5 h-px bg-border-default" />

              {wmtsPublishMode === 'layer' ? (
                <>
                  <label className="text-[11px] font-medium text-text-secondary">Название</label>
                  <input
                    value={layerName}
                    onChange={(e) => setLayerName(e.target.value)}
                    className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <label className="text-[11px] font-medium text-text-secondary">Код</label>
                      <input
                        value={layerCode}
                        onChange={(e) => setLayerCode(sanitizeCode(e.target.value))}
                        placeholder="latin_letters_and_digits"
                        className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-[11px] font-medium text-text-secondary">Группа (необязательно)</label>
                      <select
                        value={layerGroup}
                        onChange={(e) => setLayerGroup(e.target.value)}
                        className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                      >
                        <option value="">Без группы</option>
                        {layerGroups.map((g) => (
                          <option key={g.id} value={g.name}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <label className="mt-5 block text-[11px] font-medium text-text-secondary">
                    Прозрачность: {Math.round(wmtsOpacity * 100)}%
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={wmtsOpacity}
                    onChange={(e) => setWmtsOpacity(Number(e.target.value))}
                    className="mt-2 w-full accent-action-primary"
                  />
                </>
              ) : (
                <>
                  <label className="text-[11px] font-medium text-text-secondary">Название</label>
                  <input
                    value={wmtsBasemapName}
                    onChange={(e) => setWmtsBasemapName(e.target.value)}
                    className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                  <label className="mt-5 flex cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={wmtsIsDefault}
                      onChange={(e) => setWmtsIsDefault(e.target.checked)}
                      className="size-[18px] rounded-[5px] accent-action-primary"
                    />
                    <span className="text-xs font-medium text-text-primary">Сделать подложкой по умолчанию</span>
                  </label>
                </>
              )}

              {wmtsCommitError && <p className="mt-4 text-xs font-medium text-status-danger">{wmtsCommitError}</p>}
            </section>

            <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Источник</h2>
              <div className="mt-3 flex flex-col gap-2.5">
                <MetaRow label="Слой на сервере" value={selectedOgcLayer?.name ?? '—'} />
                <MetaRow label="Служба" value="WMTS" />
              </div>
              <p className="mt-5 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                Набор тайловых матриц считывается из GetCapabilities один раз при публикации и сохраняется — при
                просмотре карты повторный запрос capabilities не выполняется.
              </p>
            </section>
          </div>
        </div>
      )}

      {step === 'db-connection' && (
        <div className="grid flex-1 grid-cols-[440px_1fr_360px] gap-6 px-8 py-6">
          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Параметры подключения</h2>

            <div className="mt-5 grid grid-cols-[1fr_120px] gap-3">
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Хост</label>
                <input
                  value={dbHost}
                  onChange={(e) => {
                    setDbHost(e.target.value);
                    setDbTestStatus('idle');
                  }}
                  placeholder="192.168.1.187"
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Порт</label>
                <input
                  type="number"
                  value={dbPort}
                  onChange={(e) => {
                    setDbPort(Number(e.target.value) || 5432);
                    setDbTestStatus('idle');
                  }}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                />
              </div>
            </div>

            <label className="mt-4 text-[11px] font-medium text-text-secondary">База данных</label>
            <input
              value={dbName}
              onChange={(e) => {
                setDbName(e.target.value);
                setDbTestStatus('idle');
              }}
              placeholder="gis_test_db"
              className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
            />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Пользователь</label>
                <input
                  value={dbUser}
                  onChange={(e) => {
                    setDbUser(e.target.value);
                    setDbTestStatus('idle');
                  }}
                  placeholder="gis_test"
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Пароль</label>
                <input
                  type="password"
                  value={dbPassword}
                  onChange={(e) => {
                    setDbPassword(e.target.value);
                    setDbTestStatus('idle');
                  }}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                />
              </div>
            </div>

            <label className="mt-4 text-[11px] font-medium text-text-secondary">SSL</label>
            <select
              value={dbSslMode}
              onChange={(e) => {
                setDbSslMode(e.target.value as 'disable' | 'prefer' | 'require');
                setDbTestStatus('idle');
              }}
              className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
            >
              <option value="prefer">По возможности</option>
              <option value="require">Обязательно</option>
              <option value="disable">Отключить</option>
            </select>

            <button
              type="button"
              onClick={handleTestDbConnection}
              disabled={!dbHost.trim() || !dbName.trim() || !dbUser.trim() || dbTestStatus === 'testing'}
              className="mt-6 flex h-10 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {dbTestStatus === 'testing' ? 'Проверка…' : 'Проверить подключение'}
            </button>

            {dbTestStatus === 'success' && (
              <div className="mt-3 flex h-[26px] w-fit items-center rounded-lg bg-status-success px-2.5 text-[11px] font-medium text-text-inverse">
                Соединение установлено — найдено таблиц: {dbTables.length}
              </div>
            )}
            {dbTestStatus === 'error' && (
              <p className="mt-3 text-xs font-medium text-status-danger">{dbTestError}</p>
            )}

            <p className="mt-5 text-[11px] text-text-secondary">
              Пароль нигде не сохраняется — соединение используется только на время проверки и разбора выбранной
              таблицы, слой публикуется как обычный внутренний снимок данных.
            </p>
          </section>

          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Таблицы с геометрией</h2>
            {dbTestStatus !== 'success' ? (
              <p className="mt-6 text-xs text-text-secondary">
                Сначала проверьте подключение — список таблиц появится после успешного обращения к серверу.
              </p>
            ) : dbTables.length === 0 ? (
              <p className="mt-6 text-xs text-text-secondary">
                На сервере не найдено ни одной таблицы с геометрической колонкой (представление geometry_columns
                пусто).
              </p>
            ) : (
              <div className="mt-4 flex flex-col gap-1 overflow-y-auto">
                {dbTables.map((t) => {
                  const key = dbTableKey(t);
                  return (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        type="radio"
                        name="db-table"
                        checked={selectedDbTableKey === key}
                        onChange={() => setSelectedDbTableKey(key)}
                        className="size-4 accent-action-primary"
                      />
                      <span className="flex-1 truncate">
                        <span className="block text-xs font-medium text-text-primary">
                          {t.schema_name}.{t.table_name}
                        </span>
                        <span className="block text-[10px] text-text-secondary">
                          {t.geometry_type} · EPSG:{t.srid || '—'} · {t.row_count.toLocaleString('ru-RU')} объектов
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {dbAnalyzeError && <p className="mt-4 text-xs font-medium text-status-danger">{dbAnalyzeError}</p>}
          </section>

          <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Выбранная таблица</h2>
            {selectedDbTable ? (
              <>
                <p className="mt-3 text-[13px] font-semibold text-text-primary">
                  {selectedDbTable.schema_name}.{selectedDbTable.table_name}
                </p>
                <div className="mt-4 flex flex-col gap-2.5">
                  <MetaRow label="Геометрия" value={selectedDbTable.geometry_type} />
                  <MetaRow label="Колонка" value={selectedDbTable.geometry_column} />
                  <MetaRow label="SRID" value={String(selectedDbTable.srid || '—')} />
                  <MetaRow label="Объектов" value={selectedDbTable.row_count.toLocaleString('ru-RU')} />
                </div>
                <p className="mt-5 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                  Геометрия автоматически приводится к EPSG:4326 при чтении.
                </p>
              </>
            ) : (
              <p className="mt-4 text-xs text-text-secondary">Выберите таблицу в списке слева</p>
            )}
          </section>
        </div>
      )}

      {step === 'raster-connection' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Растровый источник</h2>
            <p className="mt-1 text-xs text-text-secondary">
              XYZ, TMS и MBTiles-серверы, отдающие тайлы по URL-шаблону — сохраняется как обычный слой без
              атрибутов, вкл/выкл и прозрачность как у любого другого слоя. Для отдельного файла Cloud-Optimized
              GeoTIFF (COG) поддержка ещё не реализована.
            </p>

            <label className="mt-5 text-[11px] font-medium text-text-secondary">URL-шаблон</label>
            <input
              value={rasterUrlTemplate}
              onChange={(e) => {
                setRasterUrlTemplate(e.target.value);
                setRasterTemplateError(null);
              }}
              placeholder="https://tile.example.com/{z}/{x}/{y}.png"
              className="mt-1.5 h-11 w-full rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
            />
            <p className="mt-1.5 text-[10px] text-text-secondary">
              Для TMS используйте {'{-y}'} вместо {'{y}'} — строки тайлов у TMS нумеруются снизу вверх.
            </p>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Размер тайла</label>
                <select
                  value={rasterTileSize}
                  onChange={(e) => setRasterTileSize(Number(e.target.value) as 256 | 512)}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                >
                  <option value={256}>256 px</option>
                  <option value={512}>512 px</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Мин. zoom</label>
                <input
                  type="number"
                  value={rasterMinZoom}
                  onChange={(e) => setRasterMinZoom(Number(e.target.value) || 0)}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[11px] font-medium text-text-secondary">Макс. zoom</label>
                <input
                  type="number"
                  value={rasterMaxZoom}
                  onChange={(e) => setRasterMaxZoom(Number(e.target.value) || 19)}
                  className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                />
              </div>
            </div>

            {rasterTemplateError && <p className="mt-4 text-xs font-medium text-status-danger">{rasterTemplateError}</p>}

            <p className="mt-5 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
              Нужна подложка (растр во весь экран под остальными слоями, единственная активная одновременно)?
              Настройте её в «Настройки карты → Подложки» — там та же логика на URL-шаблоне.
            </p>
          </div>
        </div>
      )}

      {step === 'raster-publish' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-[1fr_320px] gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Название и стиль</h2>
              <label className="mt-4 text-[11px] font-medium text-text-secondary">Название</label>
              <input
                value={layerName}
                onChange={(e) => setLayerName(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Код</label>
                  <input
                    value={layerCode}
                    onChange={(e) => setLayerCode(sanitizeCode(e.target.value))}
                    placeholder="latin_letters_and_digits"
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Группа (необязательно)</label>
                  <select
                    value={layerGroup}
                    onChange={(e) => setLayerGroup(e.target.value)}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  >
                    <option value="">Без группы</option>
                    {layerGroups.map((g) => (
                      <option key={g.id} value={g.name}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="mt-5 block text-[11px] font-medium text-text-secondary">
                Прозрачность: {Math.round(rasterOpacity * 100)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={rasterOpacity}
                onChange={(e) => setRasterOpacity(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />

              {rasterCommitError && <p className="mt-4 text-xs font-medium text-status-danger">{rasterCommitError}</p>}
            </section>

            <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Источник</h2>
              <div className="mt-3 flex flex-col gap-2.5">
                <MetaRow label="URL-шаблон" value={rasterUrlTemplate || '—'} />
                <MetaRow label="Тайл" value={`${rasterTileSize} px`} />
                <MetaRow label="Zoom" value={`${rasterMinZoom}–${rasterMaxZoom}`} />
              </div>
            </section>
          </div>
        </div>
      )}

      {step === 'arcgis-connection' && (
        <div className="grid flex-1 grid-cols-[440px_1fr_360px] gap-6 px-8 py-6">
          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Параметры подключения</h2>
            <label className="mt-5 text-[11px] font-medium text-text-secondary">URL сервиса</label>
            <input
              value={arcgisUrl}
              onChange={(e) => {
                setArcgisUrl(e.target.value);
                setArcgisTestStatus('idle');
              }}
              placeholder=".../rest/services/Xxx/MapServer"
              className="mt-1.5 h-11 rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
            />
            <p className="mt-1.5 text-[10px] text-text-secondary">Базовый URL MapServer или FeatureServer, без слоя в конце.</p>

            <button
              type="button"
              onClick={handleTestArcgisConnection}
              disabled={!arcgisUrl.trim() || arcgisTestStatus === 'testing'}
              className="mt-6 flex h-10 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {arcgisTestStatus === 'testing' ? 'Проверка…' : 'Проверить подключение'}
            </button>

            {arcgisTestStatus === 'success' && (
              <div className="mt-3 flex h-[26px] w-fit items-center rounded-lg bg-status-success px-2.5 text-[11px] font-medium text-text-inverse">
                Соединение установлено — слоёв: {arcgisLayers.length}
                {arcgisTileUrlTemplate ? ', есть тайловое покрытие' : ''}
              </div>
            )}
            {arcgisTestStatus === 'error' && <p className="mt-3 text-xs font-medium text-status-danger">{arcgisTestError}</p>}

            {arcgisTestStatus === 'success' && arcgisTileUrlTemplate && (
              <>
                <div className="my-5 h-px bg-border-default" />
                <p className="text-[13px] font-semibold text-text-primary">Тайловое покрытие</p>
                <p className="mt-1 text-[11px] text-text-secondary">
                  Сервис отдаёт кэшированные тайлы — можно опубликовать как растровый слой напрямую, без выбора
                  объектов.
                </p>
                <button
                  type="button"
                  onClick={handlePublishArcgisTiles}
                  className="mt-3 flex h-10 items-center justify-center rounded-[10px] border border-action-primary text-[13px] font-medium text-action-primary hover:bg-bg-surface-secondary"
                >
                  Опубликовать как растр
                </button>
              </>
            )}
          </section>

          <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Векторные слои</h2>
            {arcgisTestStatus !== 'success' ? (
              <p className="mt-6 text-xs text-text-secondary">
                Сначала проверьте подключение — список появится после успешного обращения к серверу.
              </p>
            ) : arcgisLayers.length === 0 ? (
              <p className="mt-6 text-xs text-text-secondary">Сервис не публикует запрашиваемых векторных слоёв.</p>
            ) : (
              <>
                <div className="mt-4 flex items-center gap-2">
                  <input
                    value={arcgisLayerSearch}
                    onChange={(e) => setArcgisLayerSearch(e.target.value)}
                    placeholder="Поиск по названию слоя"
                    className="h-10 flex-1 rounded-[10px] bg-bg-surface-secondary px-3 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                  />
                </div>
                <div className="mt-4 flex flex-col gap-1 overflow-y-auto">
                  {filteredArcgisLayers.map((l) => (
                    <label
                      key={l.name}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        type="radio"
                        name="arcgis-layer"
                        checked={selectedArcgisLayerName === l.name}
                        onChange={() => setSelectedArcgisLayerName(l.name)}
                        className="size-4 accent-action-primary"
                      />
                      <span className="flex-1 truncate">
                        <span className="block text-xs font-medium text-text-primary">{l.title}</span>
                        <span className="block text-[10px] text-text-secondary">
                          id {l.name} · {String(l.extra.geometry_type ?? '')}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
            {arcgisAnalyzeError && <p className="mt-4 text-xs font-medium text-status-danger">{arcgisAnalyzeError}</p>}
          </section>

          <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Выбранный слой</h2>
            {selectedArcgisLayer ? (
              <div className="mt-3 flex flex-col gap-2.5">
                <MetaRow label="Название" value={selectedArcgisLayer.title} />
                <MetaRow label="Id на сервере" value={selectedArcgisLayer.name} />
                <MetaRow label="Геометрия" value={String(selectedArcgisLayer.extra.geometry_type ?? '—')} />
              </div>
            ) : (
              <p className="mt-4 text-xs text-text-secondary">Выберите слой в списке слева</p>
            )}
          </section>
        </div>
      )}

      {step === 'file-upload' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[560px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Файл</h2>
            <p className="mt-1 text-xs text-text-secondary">
              Shapefile — архивом .zip (.shp + .dbf + .shx + .prj), либо .geojson/.json
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,.geojson,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelected(file);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={analyzing}
              className="mt-4 flex h-28 w-full flex-col items-center justify-center gap-2 rounded-[14px] border-2 border-dashed border-border-default bg-bg-surface-secondary text-center hover:border-action-primary disabled:cursor-not-allowed"
            >
              <span className="text-2xl text-action-primary">⇧</span>
              <span className="text-xs font-medium text-text-primary">
                {analyzing ? 'Разбираем файл…' : fileName ? fileName : 'Выберите файл или перетащите сюда'}
              </span>
            </button>

            {analyzeError && <p className="mt-3 text-xs font-medium text-status-danger">{analyzeError}</p>}
          </div>
        </div>
      )}

      {step === 'file-structure' && analyzeResult && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-2 gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">
                {analyzeResult.format === 'geojson' || analyzeResult.format === 'shapefile'
                  ? 'Что нашли в файле'
                  : 'Что нашли в источнике'}
              </h2>
              <div className="mt-4 flex flex-col gap-2.5">
                <MetaRow
                  label="Формат"
                  value={
                    {
                      database: 'PostgreSQL / PostGIS',
                      shapefile: 'Shapefile',
                      geojson: 'GeoJSON',
                      wfs: 'WFS',
                      ogc_api: 'OGC API Features',
                      arcgis: 'ArcGIS REST',
                    }[analyzeResult.format]
                  }
                />
                <MetaRow label="Геометрия" value={analyzeResult.geometry_type} />
                <MetaRow label="Объектов" value={analyzeResult.feature_count.toLocaleString('ru-RU')} />
                <MetaRow label="Исходная CRS" value={`EPSG:${analyzeResult.source_srid}`} />
                <MetaRow label="Полей" value={String(analyzeResult.fields.length)} />
              </div>
              <p className="mt-4 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                Геометрия автоматически приводится к EPSG:4326 при сохранении.
              </p>
            </section>

            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Название слоя</h2>
              <label className="mt-4 text-[11px] font-medium text-text-secondary">Название</label>
              <input
                value={layerName}
                onChange={(e) => setLayerName(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Код</label>
                  <input
                    value={layerCode}
                    onChange={(e) => setLayerCode(sanitizeCode(e.target.value))}
                    placeholder="latin_letters_and_digits"
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Группа (необязательно)</label>
                  <select
                    value={layerGroup}
                    onChange={(e) => setLayerGroup(e.target.value)}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  >
                    <option value="">Без группы</option>
                    {layerGroups.map((g) => (
                      <option key={g.id} value={g.name}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  {layerGroups.length === 0 && (
                    <p className="mt-1 text-[10px] text-text-secondary">
                      Группы создаются в панели «Слои и фильтры» на карте или в настройках карты
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {step === 'file-semantics' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[1000px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Семантика полей</h2>
            <p className="mt-1 text-xs text-text-secondary">
              Отметьте, как каждое поле ведёт себя в реестре: участвует ли в поиске, фильтрах, показывается ли в
              таблице и карточке объекта
            </p>

            {fieldOverrides.length === 0 ? (
              <p className="mt-6 text-xs text-text-secondary">
                В файле не найдено атрибутивных полей — будет импортирована только геометрия
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <div className="grid min-w-[820px] grid-cols-[24px_1.2fr_1fr_110px_74px_74px_74px_74px_74px] items-center gap-2 border-b border-border-default pb-2 text-[10px] font-semibold uppercase text-text-secondary">
                  <span />
                  <span>Поле</span>
                  <span>Имя</span>
                  <span>Тип</span>
                  <span>Поиск</span>
                  <span>Фильтр</span>
                  <span>Таблица</span>
                  <span>Попап</span>
                  <span>Обяз.</span>
                </div>
                <div className="mt-1 flex flex-col">
                  {fieldOverrides.map((field, i) => (
                    <div
                      key={field.code}
                      className="grid min-w-[820px] grid-cols-[24px_1.2fr_1fr_110px_74px_74px_74px_74px_74px] items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        type="checkbox"
                        checked={field.include}
                        onChange={(e) =>
                          setFieldOverrides((prev) => prev.map((f, idx) => (idx === i ? { ...f, include: e.target.checked } : f)))
                        }
                        className="size-[18px] rounded-[5px] accent-action-primary"
                      />
                      <span className="truncate text-xs text-text-secondary" title={field.code}>
                        {field.code}
                      </span>
                      <input
                        value={field.name}
                        disabled={!field.include}
                        onChange={(e) =>
                          setFieldOverrides((prev) => prev.map((f, idx) => (idx === i ? { ...f, name: e.target.value } : f)))
                        }
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none disabled:opacity-40"
                      />
                      <DataTypeSelect
                        dataType={field.data_type}
                        dictionaryId={field.dictionary_id}
                        dictionaries={dictionaries}
                        disabled={!field.include}
                        onChange={(dataType, dictionaryId) =>
                          setFieldOverrides((prev) =>
                            prev.map((f, idx) => (idx === i ? { ...f, data_type: dataType, dictionary_id: dictionaryId } : f)),
                          )
                        }
                      />
                      {(
                        [
                          ['searchable', field.searchable],
                          ['filterable', field.filterable],
                          ['visible_in_table', field.visible_in_table],
                          ['visible_in_popup', field.visible_in_popup],
                          ['required', field.required],
                        ] as [keyof FieldOverrideState, boolean][]
                      ).map(([key, value]) => (
                        <input
                          key={key}
                          type="checkbox"
                          checked={value}
                          disabled={!field.include}
                          onChange={(e) =>
                            setFieldOverrides((prev) => prev.map((f, idx) => (idx === i ? { ...f, [key]: e.target.checked } : f)))
                          }
                          className="mx-auto size-[18px] rounded-[5px] accent-action-primary disabled:opacity-30"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'file-style' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-[1fr_320px] gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Стиль слоя</h2>
              <p className="mt-1 text-xs text-text-secondary">Простой стиль — один цвет на весь слой. Категоризация по полю появится позже.</p>

              <label className="mt-5 text-[11px] font-medium text-text-secondary">Цвет</label>
              <div className="mt-1.5 flex gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setStyleColor(color)}
                    title={color}
                    className={`size-8 rounded-full border-2 ${styleColor === color ? 'border-text-primary' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              <label className="mt-4 block text-[11px] font-medium text-text-secondary">
                Прозрачность заливки: {Math.round(styleOpacity * 100)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={styleOpacity}
                onChange={(e) => setStyleOpacity(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />

              <label className="mt-4 block text-[11px] font-medium text-text-secondary">Толщина линии: {strokeWidth} px</label>
              <input
                type="range"
                min={0.5}
                max={6}
                step={0.5}
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />
            </section>

            <section className="flex h-fit flex-col items-center rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="self-start text-base font-semibold text-text-primary">Предпросмотр</h2>
              <div className="mt-4 flex h-40 w-full items-center justify-center rounded-xl bg-bg-surface-secondary">
                <div
                  className="h-24 w-32 rounded-md"
                  style={{
                    backgroundColor: `${styleColor}${Math.round(styleOpacity * 255)
                      .toString(16)
                      .padStart(2, '0')}`,
                    border: `${strokeWidth}px solid ${styleColor}`,
                  }}
                />
              </div>
            </section>
          </div>
        </div>
      )}

      {step === 'file-access' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Доступ к слою</h2>
            <div className="mt-4">
              <LayerAccessEditor
                rows={accessRows}
                members={accessMembers}
                groups={accessGroups}
                onAdd={handleAddAccessRow}
                onRemove={handleRemoveAccessRow}
              />
            </div>
          </div>
        </div>
      )}

      {step === 'file-publish' && analyzeResult && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Проверьте и опубликуйте</h2>
            <div className="mt-4 flex flex-col gap-2.5">
              <MetaRow label="Название" value={layerName || '—'} />
              <MetaRow label="Код" value={layerCode || '—'} />
              <MetaRow label="Группа" value={layerGroup || 'Без группы'} />
              <MetaRow label="Геометрия" value={analyzeResult.geometry_type} />
              <MetaRow label="Объектов" value={analyzeResult.feature_count.toLocaleString('ru-RU')} />
              <MetaRow label="Полей включено" value={`${includedFieldCount} из ${fieldOverrides.length}`} />
            </div>

            <div className="my-4 h-px bg-border-default" />

            <p className="text-[11px] font-medium text-text-secondary">Стиль</p>
            <div className="mt-2 flex items-center gap-3">
              <span className="size-6 rounded-full" style={{ backgroundColor: styleColor }} />
              <span className="text-xs text-text-primary">
                Заливка {Math.round(styleOpacity * 100)}%, линия {strokeWidth} px
              </span>
            </div>

            {commitError && <p className="mt-4 text-xs font-medium text-status-danger">{commitError}</p>}

            <p className="mt-4 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
              После публикации слой сразу появится на карте и в реестре данных.
            </p>
          </div>
        </div>
      )}

      {step === 'empty-structure' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-2 gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Тип геометрии</h2>
              <p className="mt-1 text-xs text-text-secondary">
                Определяет, какие объекты можно будет рисовать в этом слое — изменить тип после создания нельзя
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {geometryTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEmptyGeometryType(opt.value)}
                    className={`rounded-[10px] border p-3 text-left text-xs font-medium transition-colors ${
                      emptyGeometryType === opt.value
                        ? 'border-action-primary bg-bg-surface-secondary text-text-primary'
                        : 'border-border-default bg-bg-surface text-text-secondary hover:bg-bg-surface-secondary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-4 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
                Геометрия объектов хранится в EPSG:4326, как и у остальных слоёв.
              </p>
            </section>

            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Название слоя</h2>
              <label className="mt-4 text-[11px] font-medium text-text-secondary">Название</label>
              <input
                value={layerName}
                onChange={(e) => {
                  setLayerName(e.target.value);
                  if (!layerCode.trim()) setLayerCode(slugify(e.target.value, 'layer'));
                }}
                className="mt-1.5 h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Код</label>
                  <input
                    value={layerCode}
                    onChange={(e) => setLayerCode(sanitizeCode(e.target.value))}
                    placeholder="latin_letters_and_digits"
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Группа (необязательно)</label>
                  <select
                    value={layerGroup}
                    onChange={(e) => setLayerGroup(e.target.value)}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
                  >
                    <option value="">Без группы</option>
                    {layerGroups.map((g) => (
                      <option key={g.id} value={g.name}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {step === 'empty-fields' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[1000px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-text-primary">Поля слоя</h2>
                <p className="mt-1 text-xs text-text-secondary">
                  Атрибуты объектов — колонки таблицы слоя и их типы данных
                </p>
              </div>
              <button
                type="button"
                onClick={addEmptyField}
                className="flex h-9 shrink-0 items-center rounded-[9px] bg-action-primary px-3 text-xs font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                + Добавить поле
              </button>
            </div>

            {emptyFields.length === 0 ? (
              <p className="mt-6 text-xs text-text-secondary">
                Пока нет ни одного поля — слой будет содержать только геометрию. Добавьте поле, если нужны атрибуты.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <div className="grid min-w-[900px] grid-cols-[1fr_1fr_110px_60px_60px_60px_60px_60px_32px] items-center gap-2 border-b border-border-default pb-2 text-[10px] font-semibold uppercase text-text-secondary">
                  <span>Код</span>
                  <span>Имя</span>
                  <span>Тип</span>
                  <span>Поиск</span>
                  <span>Фильтр</span>
                  <span>Таблица</span>
                  <span>Попап</span>
                  <span>Обяз.</span>
                  <span />
                </div>
                <div className="mt-1 flex flex-col">
                  {emptyFields.map((field, i) => (
                    <div
                      key={i}
                      className="grid min-w-[900px] grid-cols-[1fr_1fr_110px_60px_60px_60px_60px_60px_32px] items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-bg-surface-secondary"
                    >
                      <input
                        value={field.code}
                        onChange={(e) => {
                          const code = sanitizeCode(e.target.value);
                          setEmptyFields((prev) =>
                            prev.map((f, idx) => (idx === i ? { ...f, code, codeTouched: true } : f)),
                          );
                        }}
                        placeholder="code"
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                      />
                      <input
                        value={field.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setEmptyFields((prev) =>
                            prev.map((f, idx) =>
                              idx === i
                                ? { ...f, name, code: f.codeTouched ? f.code : slugify(name, `field_${i + 1}`) }
                                : f,
                            ),
                          );
                        }}
                        placeholder="Название"
                        className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                      />
                      <DataTypeSelect
                        dataType={field.data_type}
                        dictionaryId={field.dictionary_id}
                        dictionaries={dictionaries}
                        onChange={(dataType, dictionaryId) =>
                          setEmptyFields((prev) =>
                            prev.map((f, idx) => (idx === i ? { ...f, data_type: dataType, dictionary_id: dictionaryId } : f)),
                          )
                        }
                      />
                      {(
                        [
                          ['searchable', field.searchable],
                          ['filterable', field.filterable],
                          ['visible_in_table', field.visible_in_table],
                          ['visible_in_popup', field.visible_in_popup],
                          ['required', field.required],
                        ] as [keyof FieldOverrideState, boolean][]
                      ).map(([key, value]) => (
                        <input
                          key={key}
                          type="checkbox"
                          checked={value}
                          onChange={(e) =>
                            setEmptyFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, [key]: e.target.checked } : f)))
                          }
                          className="mx-auto size-[18px] rounded-[5px] accent-action-primary"
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => removeEmptyField(i)}
                        title="Удалить поле"
                        className="mx-auto flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-status-danger"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'empty-style' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto grid max-w-[900px] grid-cols-[1fr_320px] gap-6">
            <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-base font-semibold text-text-primary">Стиль слоя</h2>
              <p className="mt-1 text-xs text-text-secondary">Простой стиль — один цвет на весь слой. Категоризация по полю появится позже.</p>

              <label className="mt-5 text-[11px] font-medium text-text-secondary">Цвет</label>
              <div className="mt-1.5 flex gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setStyleColor(color)}
                    title={color}
                    className={`size-8 rounded-full border-2 ${styleColor === color ? 'border-text-primary' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              <label className="mt-4 block text-[11px] font-medium text-text-secondary">
                Прозрачность заливки: {Math.round(styleOpacity * 100)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={styleOpacity}
                onChange={(e) => setStyleOpacity(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />

              <label className="mt-4 block text-[11px] font-medium text-text-secondary">Толщина линии: {strokeWidth} px</label>
              <input
                type="range"
                min={0.5}
                max={6}
                step={0.5}
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="mt-2 w-full accent-action-primary"
              />
            </section>

            <section className="flex h-fit flex-col items-center rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="self-start text-base font-semibold text-text-primary">Предпросмотр</h2>
              <div className="mt-4 flex h-40 w-full items-center justify-center rounded-xl bg-bg-surface-secondary">
                <div
                  className="h-24 w-32 rounded-md"
                  style={{
                    backgroundColor: `${styleColor}${Math.round(styleOpacity * 255)
                      .toString(16)
                      .padStart(2, '0')}`,
                    border: `${strokeWidth}px solid ${styleColor}`,
                  }}
                />
              </div>
            </section>
          </div>
        </div>
      )}

      {step === 'empty-access' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Доступ к слою</h2>
            <div className="mt-4">
              <LayerAccessEditor
                rows={accessRows}
                members={accessMembers}
                groups={accessGroups}
                onAdd={handleAddAccessRow}
                onRemove={handleRemoveAccessRow}
              />
            </div>
          </div>
        </div>
      )}

      {step === 'empty-publish' && (
        <div className="flex-1 px-8 py-8">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-base font-semibold text-text-primary">Проверьте и опубликуйте</h2>
            <div className="mt-4 flex flex-col gap-2.5">
              <MetaRow label="Название" value={layerName || '—'} />
              <MetaRow label="Код" value={layerCode || '—'} />
              <MetaRow label="Группа" value={layerGroup || 'Без группы'} />
              <MetaRow label="Геометрия" value={geometryTypeOptions.find((o) => o.value === emptyGeometryType)?.label ?? emptyGeometryType} />
              <MetaRow label="Объектов" value="0 — рисуются на карте" />
              <MetaRow label="Полей" value={String(emptyFields.length)} />
            </div>

            <div className="my-4 h-px bg-border-default" />

            <p className="text-[11px] font-medium text-text-secondary">Стиль</p>
            <div className="mt-2 flex items-center gap-3">
              <span className="size-6 rounded-full" style={{ backgroundColor: styleColor }} />
              <span className="text-xs text-text-primary">
                Заливка {Math.round(styleOpacity * 100)}%, линия {strokeWidth} px
              </span>
            </div>

            {createLayerError && <p className="mt-4 text-xs font-medium text-status-danger">{createLayerError}</p>}

            <p className="mt-4 rounded-lg bg-bg-surface-secondary p-3 text-[11px] text-text-secondary">
              Слой создастся пустым и сразу появится на карте — рисуйте объекты через меню слоя («⋮» → «Нарисовать объект»).
            </p>
          </div>
        </div>
      )}

      {step === 'ogc-connection' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 2 из 7</p>
          <button
            type="button"
            onClick={handleOgcNext}
            disabled={!selectedOgcLayer || ogcAnalyzing}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {ogcAnalyzing ? 'Загружаем объекты…' : 'Далее'}
          </button>
        </footer>
      )}

      {step === 'ogc-wmts-publish' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('ogc-connection')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 7 из 7</p>
          <button
            type="button"
            onClick={handleCommitWmts}
            disabled={
              wmtsCommitting ||
              (wmtsPublishMode === 'basemap' ? !wmtsBasemapName.trim() : !layerName.trim() || !layerCode.trim())
            }
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {wmtsCommitting ? 'Публикуем…' : 'Опубликовать'}
          </button>
        </footer>
      )}

      {step === 'ogc-wms-publish' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('ogc-connection')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 7 из 7</p>
          <button
            type="button"
            onClick={handleCommitWmsLayer}
            disabled={!layerName.trim() || !layerCode.trim() || wmsCommitting}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {wmsCommitting ? 'Публикуем…' : 'Опубликовать'}
          </button>
        </footer>
      )}

      {step === 'db-connection' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 2 из 7</p>
          <button
            type="button"
            onClick={handleAnalyzeDbTable}
            disabled={!selectedDbTable || dbAnalyzing}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {dbAnalyzing ? 'Читаем таблицу…' : 'Далее'}
          </button>
        </footer>
      )}

      {step === 'raster-connection' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 2 из 7</p>
          <button
            type="button"
            onClick={handleRasterNext}
            disabled={!rasterUrlTemplate.trim()}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'raster-publish' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('raster-connection')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 7 из 7</p>
          <button
            type="button"
            onClick={handleCommitRaster}
            disabled={!layerName.trim() || !layerCode.trim() || rasterCommitting}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {rasterCommitting ? 'Публикуем…' : 'Опубликовать'}
          </button>
        </footer>
      )}

      {step === 'arcgis-connection' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          {arcgisAnalyzeError ? (
            <p className="mx-auto text-xs font-medium text-status-danger">{arcgisAnalyzeError}</p>
          ) : (
            <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 2 из 7</p>
          )}
          <button
            type="button"
            onClick={handleAnalyzeArcgisLayer}
            disabled={!selectedArcgisLayer || arcgisAnalyzing}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {arcgisAnalyzing ? 'Загружаем объекты…' : 'Далее'}
          </button>
        </footer>
      )}

      {step === 'source' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <Link
            to={returnTo}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Отмена
          </Link>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 1 из 7</p>
          <button
            type="button"
            onClick={() => {
              if (!selectedCard.available) return;
              if (selectedKind === 'file') setStep('file-upload');
              else if (selectedKind === 'empty') setStep('empty-structure');
              else if (selectedKind === 'postgis') setStep('db-connection');
              else if (selectedKind === 'raster') setStep('raster-connection');
              else if (selectedKind === 'rest') setStep('arcgis-connection');
              else setStep('ogc-connection');
            }}
            disabled={!selectedCard.available}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Продолжить
          </button>
        </footer>
      )}

      {step === 'file-upload' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 2 из 7</p>
        </footer>
      )}

      {step === 'file-structure' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('file-upload')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 3 из 7</p>
          <button
            type="button"
            onClick={() => setStep('file-semantics')}
            disabled={!layerName.trim() || !layerCode.trim()}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'file-semantics' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('file-structure')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 4 из 7</p>
          <button
            type="button"
            onClick={() => setStep('file-style')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'file-style' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('file-semantics')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 5 из 7</p>
          <button
            type="button"
            onClick={() => setStep('file-access')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'file-access' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('file-style')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 6 из 7</p>
          <button
            type="button"
            onClick={() => setStep('file-publish')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'file-publish' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('file-access')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 7 из 7</p>
          <button
            type="button"
            onClick={handleCommitFileImport}
            disabled={!analyzeResult || !layerName.trim() || !layerCode.trim() || committing}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {committing ? 'Публикуем…' : 'Опубликовать'}
          </button>
        </footer>
      )}

      {step === 'empty-structure' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('source')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 3 из 7</p>
          <button
            type="button"
            onClick={() => setStep('empty-fields')}
            disabled={!layerName.trim() || !layerCode.trim()}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'empty-fields' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('empty-structure')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 4 из 7</p>
          <button
            type="button"
            onClick={() => setStep('empty-style')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'empty-style' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('empty-fields')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 5 из 7</p>
          <button
            type="button"
            onClick={() => setStep('empty-access')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'empty-access' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('empty-style')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-medium text-text-secondary">Шаг 6 из 7</p>
          <button
            type="button"
            onClick={() => setStep('empty-publish')}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover"
          >
            Далее
          </button>
        </footer>
      )}

      {step === 'empty-publish' && (
        <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
          <button
            type="button"
            onClick={() => setStep('empty-access')}
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Назад
          </button>
          <p className="mx-auto text-xs font-semibold text-text-secondary">Шаг 7 из 7</p>
          <button
            type="button"
            onClick={handleCreateEmptyLayer}
            disabled={!layerName.trim() || !layerCode.trim() || creatingLayer}
            className="flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creatingLayer ? 'Создаём…' : 'Создать слой'}
          </button>
        </footer>
      )}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[11px] text-text-secondary">{label}</span>
      <span className="text-[11px] font-medium text-text-primary">{value}</span>
    </div>
  );
}
